/**
 * WebCodecs + mediabunny adapter — the ONLY client render path (implementation-plan.md §2.2).
 *
 * The streaming pipeline, one frame in flight at a time:
 *
 *   source (Blob | URL) → mediabunny Input demux
 *     → VideoSampleSink: decode the tIn..tOut segment to frames
 *       → compositeFrame(canvas, frame, overlay)   ← canvas IS the overlay engine
 *       → CanvasSource.add(t, dur)                 ← WebCodecs VideoEncoder (H.264)
 *     → audio: AudioSampleSink → AAC re-encode, or EncodedPacketSink → passthrough
 *     → Mp4OutputFormat muxer → MP4 Blob
 *
 * No engine download (the codec is the browser's), no drawtext, no filter graph, and peak
 * memory stays flat because every decoded frame is closed before the next is pulled.
 *
 * ffmpeg.wasm is deliberately absent: GPL core, ~0.04x native, crashes iOS Safari on large
 * files (§4.2).
 */
import {
  ALL_FORMATS,
  AudioSampleSink,
  AudioSampleSource,
  BlobSource,
  BufferTarget,
  CanvasSource,
  EncodedAudioPacketSource,
  EncodedPacketSink,
  Input,
  Mp4OutputFormat,
  Output,
  QUALITY_HIGH,
  QUALITY_MEDIUM,
  UrlSource,
  VideoSampleSink,
  type EncodedPacket,
  type InputAudioTrack,
  type InputVideoTrack,
} from 'mediabunny';
import { compositeFrame, loadLogoImage, type OverlayAssets } from '../branding';
import { probeCapabilities, resolveAudioMode } from './probe';
import {
  H264_LADDER,
  H264_PROFILE_NAMES,
  type CapabilityReport,
  type ClipRenderer,
  type RenderOptions,
  type RenderProgress,
  type RenderRequest,
  type RenderResult,
} from './types';

/** Preview caps the long edge; export keeps the source resolution. */
export const PREVIEW_MAX_EDGE = 854;

export class WebCodecsRenderer implements ClipRenderer {
  readonly kind = 'webcodecs' as const;

  constructor(private readonly report?: CapabilityReport) {}

  probe(): Promise<CapabilityReport> {
    return probeCapabilities();
  }

  async render(request: RenderRequest, options: RenderOptions = {}): Promise<RenderResult> {
    if (request.format !== 'mp4') throw new Error(`Unsupported container: ${request.format}`);
    if (!(request.tOut > request.tIn)) throw new Error('tOut must be greater than tIn');

    const report = this.report ?? (await probeCapabilities());
    const videoCodec = pickH264(report);
    if (!videoCodec) throw new Error('No H.264 encoder available in this browser');

    const started = performance.now();
    const input = new Input({
      source:
        typeof request.sourceVideo === 'string'
          ? new UrlSource(request.sourceVideo)
          : new BlobSource(request.sourceVideo),
      formats: ALL_FORMATS,
    });

    let output: Output<Mp4OutputFormat, BufferTarget> | null = null;
    try {
      const videoTrack = await input.getPrimaryVideoTrack();
      if (!videoTrack) throw new Error('Source has no video track');

      const displayWidth = await videoTrack.getDisplayWidth();
      const displayHeight = await videoTrack.getDisplayHeight();
      const { width, height } = targetSize(
        displayWidth,
        displayHeight,
        request.quality,
        report,
      );

      const audioTrack = await input.getPrimaryAudioTrack();
      const sourceAudioCodec = audioTrack ? await audioTrack.getCodec() : null;
      // No audio track in the source at all → nothing to re-encode or pass through.
      const audioMode = audioTrack
        ? resolveAudioMode(report, request.audioTrack, sourceAudioCodec)
        : ('mute' as const);

      const canvas = createCanvas(width, height);
      const ctx = canvas.getContext('2d') as
        | CanvasRenderingContext2D
        | OffscreenCanvasRenderingContext2D
        | null;
      if (!ctx) throw new Error('Could not acquire a 2D context for compositing');

      const assets: OverlayAssets = {
        logo: request.overlay.logoUrl
          ? await loadLogoImage(request.overlay.logoUrl).catch(() => null)
          : null,
      };

      const videoSource = new CanvasSource(canvas, {
        codec: 'avc',
        // Pin the ladder winner: without this mediabunny picks its own profile.
        fullCodecString: videoCodec,
        quality: request.quality === 'preview' ? QUALITY_MEDIUM : QUALITY_HIGH,
        keyFrameInterval: 2,
      });

      output = new Output({ format: new Mp4OutputFormat(), target: new BufferTarget() });
      output.addVideoTrack(videoSource);

      let audioSource: EncodedAudioPacketSource | null = null;
      let audioReencode: { source: AudioSampleSource } | null = null;
      if (audioMode === 'reencode') {
        audioReencode = buildReencodeSource(request.quality);
        output.addAudioTrack(audioReencode.source);
      } else if (audioMode === 'passthrough') {
        audioSource = new EncodedAudioPacketSource('aac');
        output.addAudioTrack(audioSource);
      }

      await output.start();

      const duration = await encodeVideo({
        videoTrack,
        videoSource,
        ctx,
        canvas: { width, height },
        overlay: request.overlay,
        assets,
        tIn: request.tIn,
        tOut: request.tOut,
        onProgress: options.onProgress,
      });

      const audioWritten =
        audioReencode !== null
          ? await encodeAudioSamples({
              audioTrack: audioTrack as InputAudioTrack,
              source: audioReencode.source,
              tIn: request.tIn,
              tOut: request.tOut,
            })
          : audioSource !== null
            ? await passthroughAudio({
                audioTrack: audioTrack as InputAudioTrack,
                source: audioSource,
                tIn: request.tIn,
                tOut: request.tOut,
              })
            : false;

      videoSource.close();
      audioReencode?.source.close();
      audioSource?.close();

      await output.finalize();
      const buffer = output.target.buffer;
      if (!buffer) throw new Error('Muxer produced no buffer');
      const blob = new Blob([buffer], { type: 'video/mp4' });

      return {
        blob,
        duration,
        sizeBytes: blob.size,
        renderTimeMs: Math.round(performance.now() - started),
        renderer: 'webcodecs',
        // Report what actually reached the container, not what we hoped for.
        audioTrack: audioWritten ? audioMode : 'mute',
        videoCodec,
        width,
        height,
      };
    } finally {
      try {
        input.dispose();
      } catch {
        /* already disposed */
      }
      if (output && !output.target.buffer) {
        try {
          await output.cancel();
        } catch {
          /* nothing to cancel */
        }
      }
    }
  }
}

/** The highest-profile H.264 the probe found, in ladder order. */
export function pickH264(report: CapabilityReport): string | null {
  for (const codec of H264_LADDER) if (report.videoEncode.includes(codec)) return codec;
  return report.videoEncode[0] ?? null;
}

export function h264ProfileName(codec: string | null): string | null {
  return codec ? (H264_PROFILE_NAMES[codec] ?? codec) : null;
}

/** Preview downscales; export keeps the source size but never upscales past the probe size. */
export function targetSize(
  displayWidth: number,
  displayHeight: number,
  quality: 'preview' | 'export',
  _report: CapabilityReport,
): { width: number; height: number } {
  const longEdge = Math.max(displayWidth, displayHeight);
  const cap = quality === 'preview' ? PREVIEW_MAX_EDGE : Math.max(displayWidth, displayHeight);
  if (longEdge <= cap) return { width: even(displayWidth), height: even(displayHeight) };
  const scale = cap / longEdge;
  return { width: even(displayWidth * scale), height: even(displayHeight * scale) };
}

/** H.264 wants even dimensions; an odd width makes some encoders reject the config. */
function even(value: number): number {
  const rounded = Math.round(value);
  return rounded % 2 === 0 ? rounded : rounded + 1;
}

function createCanvas(width: number, height: number): HTMLCanvasElement | OffscreenCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

interface EncodeVideoArgs {
  videoTrack: InputVideoTrack;
  videoSource: CanvasSource;
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
  canvas: { width: number; height: number };
  overlay: RenderRequest['overlay'];
  assets: OverlayAssets;
  tIn: number;
  tOut: number;
  onProgress?: (progress: RenderProgress) => void;
}

/** Decode → composite → encode, one frame at a time. Returns the clip's real duration. */
async function encodeVideo(args: EncodeVideoArgs): Promise<number> {
  const { videoTrack, videoSource, ctx, canvas, overlay, assets, tIn, tOut } = args;
  const sink = new VideoSampleSink(videoTrack);
  const expectedFrames = await estimateFrames(videoTrack, tIn, tOut);

  let frames = 0;
  let duration = 0;

  for await (const sample of sink.samples(tIn, tOut)) {
    try {
      const timestamp = Math.max(0, sample.timestamp - tIn);
      const frameDuration = sample.duration > 0 ? sample.duration : 1 / 30;
      compositeFrame(ctx, sample.toCanvasImageSource(), overlay, canvas, assets);
      await videoSource.add(timestamp, frameDuration);
      duration = Math.max(duration, timestamp + frameDuration);
      frames += 1;
      args.onProgress?.({ frames, totalFrames: expectedFrames });
    } finally {
      sample.close();
    }
  }

  if (frames === 0) throw new Error('No video frames in the requested window');
  return Number(duration.toFixed(3));
}

/** Frame-count hint for the UI's per-clip bar. Cheap: packet stats, not a full decode. */
async function estimateFrames(
  track: InputVideoTrack,
  tIn: number,
  tOut: number,
): Promise<number | null> {
  try {
    const stats = await track.computePacketStats(40);
    if (!stats || !Number.isFinite(stats.averagePacketRate) || stats.averagePacketRate <= 0) {
      return null;
    }
    return Math.max(1, Math.round((tOut - tIn) * stats.averagePacketRate));
  } catch {
    return null;
  }
}

function buildReencodeSource(quality: 'preview' | 'export'): { source: AudioSampleSource } {
  return {
    source: new AudioSampleSource({
      codec: 'aac',
      quality: quality === 'preview' ? QUALITY_MEDIUM : QUALITY_HIGH,
    }),
  };
}

/** Decode the source audio segment and re-encode it to AAC-LC (§2.2). */
async function encodeAudioSamples(args: {
  audioTrack: InputAudioTrack;
  source: AudioSampleSource;
  tIn: number;
  tOut: number;
}): Promise<boolean> {
  const { audioTrack, source, tIn, tOut } = args;
  const sink = new AudioSampleSink(audioTrack);
  let written = 0;
  for await (const sample of sink.samples(tIn, tOut)) {
    try {
      sample.setTimestamp(Math.max(0, sample.timestamp - tIn));
      await source.add(sample);
      written += 1;
    } finally {
      sample.close();
    }
  }
  return written > 0;
}

/**
 * Copy the source's encoded AAC packets straight into the MP4 (§2.6 — the Safari path, where
 * there is no AudioEncoder but the source is already H.264/AAC). Timestamps are rebased to 0.
 */
async function passthroughAudio(args: {
  audioTrack: InputAudioTrack;
  source: EncodedAudioPacketSource;
  tIn: number;
  tOut: number;
}): Promise<boolean> {
  const { audioTrack, source, tIn, tOut } = args;
  const codec = await audioTrack.getCodec();
  if (codec !== 'aac') return false;
  const decoderConfig = await audioTrack.getDecoderConfig();
  const sink = new EncodedPacketSink(audioTrack);
  const start = await sink.getKeyPacket(tIn);
  const end = await sink.getPacket(tOut);

  let written = 0;
  for await (const packet of sink.packets(start ?? undefined, end ?? undefined)) {
    if (packet.timestamp + packet.duration <= tIn) continue;
    if (packet.timestamp >= tOut) break;
    const shifted = rebasePacket(packet, tIn);
    await source.add(shifted, written === 0 && decoderConfig ? { decoderConfig } : undefined);
    written += 1;
  }
  return written > 0;
}

function rebasePacket(packet: EncodedPacket, tIn: number): EncodedPacket {
  const timestamp = Math.max(0, packet.timestamp - tIn);
  return packet.clone({ timestamp });
}
